import { createClient } from '@supabase/supabase-js';

// Configuração do Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Criar cliente do Supabase
const supabase = createClient(supabaseUrl, supabaseKey);

// Verificar se as credenciais estão configuradas
const checkSupabaseConfig = () => {
  if (!supabaseUrl) {
    console.warn('⚠️ URL do Supabase não configurada. Configure REACT_APP_SUPABASE_URL no arquivo .env');
    return false;
  }
  
  if (!supabaseKey) {
    console.warn('⚠️ Chave do Supabase não configurada. Configure REACT_APP_SUPABASE_ANON_KEY no arquivo .env');
    return false;
  }
  
  return true;
};

// Função para verificar duplicidade
export const checkForDuplicates = async (formData) => {
  try {
    console.log('Verificando duplicidade no Supabase...', formData);
    
    if (!checkSupabaseConfig()) {
      console.log('Supabase não configurado, usando simulação...');
      return await simulateDuplicateCheck(formData);
    }
    
    // Verificar duplicidade por telefone
    const phoneNumbers = formData.telefone.replace(/\D/g, '');
    const { data: phoneMatches, error: phoneError } = await supabase
      .from('profissionais')
      .select('*')
      .ilike('telefone', `%${phoneNumbers}%`);
    
    if (phoneError) {
      console.error('Erro ao verificar telefone:', phoneError);
      return {
        success: false,
        message: 'Erro ao verificar duplicidade. Tente novamente.',
        error: phoneError.message
      };
    }
    
    if (phoneMatches && phoneMatches.length > 0) {
      return {
        success: true,
        isDuplicate: true,
        message: `Já existe um cadastro com este telefone. Nome: ${phoneMatches[0].nome}`,
        existingData: phoneMatches[0],
        duplicateField: 'telefone'
      };
    }
    
    // Verificar duplicidade por e-mail (se fornecido)
    if (formData.email && formData.email.trim()) {
      const { data: emailMatches, error: emailError } = await supabase
        .from('profissionais')
        .select('*')
        .eq('email', formData.email.toLowerCase());
      
      if (emailError) {
        console.error('Erro ao verificar e-mail:', emailError);
        return {
          success: false,
          message: 'Erro ao verificar duplicidade. Tente novamente.',
          error: emailError.message
        };
      }
      
      if (emailMatches && emailMatches.length > 0) {
        return {
          success: true,
          isDuplicate: true,
          message: `Já existe um cadastro com este e-mail. Nome: ${emailMatches[0].nome}`,
          existingData: emailMatches[0],
          duplicateField: 'email'
        };
      }
    }
    
    // Não é duplicata
    return {
      success: true,
      isDuplicate: false,
      message: 'Cadastro único, pode prosseguir.'
    };
    
  } catch (error) {
    console.error('Erro ao verificar duplicidade:', error);
    return {
      success: false,
      message: 'Erro ao verificar duplicidade. Tente novamente.',
      error: error.message
    };
  }
};

// Função para salvar dados no Supabase
export const saveToSupabase = async (formData) => {
  try {
    // Se não for salvamento forçado, verifica duplicidade
    if (!formData.forceSave) {
      const duplicateCheck = await checkForDuplicates(formData);
      
      if (!duplicateCheck.success) {
        return duplicateCheck;
      }
      
      if (duplicateCheck.isDuplicate) {
        return {
          success: false,
          message: duplicateCheck.message,
          isDuplicate: true,
          existingData: duplicateCheck.existingData
        };
      }
    }
    
    console.log('Salvando dados no Supabase:', formData);
    
    if (!checkSupabaseConfig()) {
      console.log('Supabase não configurado, usando simulação...');
      const response = await simulateSupabaseAPI(formData);
      return {
        success: true,
        message: 'Dados salvos com sucesso (modo simulação)!',
        data: response
      };
    }
    
    // Preparar dados para inserção
    const profissionalData = {
      nome: formData.nome,
      telefone: formData.telefone,
      email: formData.email || null,
      cidade: formData.cidade || null,
      estado: formData.estado || null,
      bairro: formData.bairro || null,
      profissoes: formData.profissoes,
      habilidades: formData.habilidades || null,
      pretensao: formData.pretensao || null, // Temporariamente comentado - adicionar coluna no Supabase
      experiencia: formData.experiencia,
      created_at: new Date().toISOString()
    };
    
    // Inserir dados no Supabase
    const { data, error } = await supabase
      .from('profissionais')
      .insert([profissionalData])
      .select();
    
    if (error) {
      console.error('Erro ao salvar no Supabase:', error);
      console.error('Dados que causaram o erro:', profissionalData);
      return {
        success: false,
        message: `Erro ao salvar dados: ${error.message}`,
        error: error.message,
        details: error
      };
    }
    
    return {
      success: true,
      message: 'Dados salvos com sucesso no Supabase!',
      data: data[0]
    };
    
  } catch (error) {
    console.error('Erro ao salvar no Supabase:', error);
    return {
      success: false,
      message: 'Erro ao salvar dados. Tente novamente.',
      error: error.message
    };
  }
};

// Função simulada para demonstração (quando Supabase não está configurado)
const simulateDuplicateCheck = async (formData) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Simula dados existentes
      const existingRecords = [
        {
          telefone: '(11) 99999-9999',
          email: 'joao@email.com',
          nome: 'João Silva'
        },
        {
          telefone: '(21) 88888-8888',
          email: 'maria@email.com',
          nome: 'Maria Santos'
        }
      ];
      
      // Verifica duplicidade por telefone
      const phoneNumbers = formData.telefone.replace(/\D/g, '');
      const existingByPhone = existingRecords.find(record => {
        const recordPhone = record.telefone.replace(/\D/g, '');
        return recordPhone === phoneNumbers;
      });
      
      // Verifica duplicidade por e-mail
      let existingByEmail = null;
      if (formData.email && formData.email.trim()) {
        existingByEmail = existingRecords.find(record => 
          record.email.toLowerCase() === formData.email.toLowerCase()
        );
      }
      
      if (existingByPhone) {
        resolve({
          success: true,
          isDuplicate: true,
          message: `Já existe um cadastro com este telefone. Nome: ${existingByPhone.nome}`,
          existingData: existingByPhone,
          duplicateField: 'telefone'
        });
        return;
      }
      
      if (existingByEmail) {
        resolve({
          success: true,
          isDuplicate: true,
          message: `Já existe um cadastro com este e-mail. Nome: ${existingByEmail.nome}`,
          existingData: existingByEmail,
          duplicateField: 'email'
        });
        return;
      }
      
      // Não é duplicata
      resolve({
        success: true,
        isDuplicate: false,
        message: 'Cadastro único, pode prosseguir.'
      });
    }, 800);
  });
};

const simulateSupabaseAPI = async (formData) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const rowData = {
        id: Math.random().toString(36).substr(2, 9),
        nome: formData.nome,
        telefone: formData.telefone,
        email: formData.email || null,
        cidade: formData.cidade || null,
        estado: formData.estado || null,
        bairro: formData.bairro || null,
        profissoes: formData.profissoes,
        habilidades: formData.habilidades || null,
        // pretensao: formData.pretensao || null, // Temporariamente comentado - adicionar coluna no Supabase
        experiencia: formData.experiencia,
        created_at: new Date().toISOString()
      };
      
      console.log('Dados simulados para Supabase:', rowData);
      resolve(rowData);
    }, 1000);
  });
};

// Função para testar a conexão com Supabase
export const testSupabaseConnection = async () => {
  try {
    console.log('Testando conexão com Supabase...');
    
    if (!checkSupabaseConfig()) {
      return {
        success: false,
        message: 'Supabase não configurado. Configure as variáveis de ambiente.'
      };
    }
    
    // Testar conexão fazendo uma consulta simples
    const { data, error } = await supabase
      .from('profissionais')
      .select('count')
      .limit(1);
    
    if (error) {
      return {
        success: false,
        message: 'Erro na conexão com Supabase',
        error: error.message
      };
    }
    
    return {
      success: true,
      message: 'Conexão com Supabase funcionando!'
    };
  } catch (error) {
    return {
      success: false,
      message: 'Erro na conexão com Supabase',
      error: error.message
    };
  }
};
